// The daily puzzle's rules of the day: which puzzle is today's and the text a
// player shares. The puzzles, the answers and every player's tries live on the
// game server (sequence-be/services/dailyPuzzle.js); this browser only ever
// gets today's board.

// Puzzle #1 is this day; a new one starts at each local midnight, as Wordle does.
const FIRST_DAY = new Date(2026, 10, 1);
const DAY_MS = 24 * 60 * 60 * 1000;
export const MAX_TRIES = 3;
export const SITE = 'https://play-sequence.com';

const midnight = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

/** Today's puzzle number, from 1. Rounding absorbs daylight-saving hours. */
export const puzzleNumber = (now = new Date()) =>
    Math.max(0, Math.round((midnight(now) - FIRST_DAY) / DAY_MS)) + 1;

/** Milliseconds until the next puzzle. */
export const msToNext = (now = new Date()) => {
    const next = midnight(now);
    next.setDate(next.getDate() + 1);
    return next - now;
};

export const KINDS = {
    win: {
        label: 'Win in 1',
        task: 'You are blue. One card in your hand completes a sequence of five. Find it.',
        miss: "That doesn't make five in a row. Look for four blue chips with one gap.",
        answer: (card) => `The ${card} fills the gap and makes five in a row.`,
    },
    block: {
        label: 'Stop them',
        task: "You are blue, and you can't win this turn. Green wins next turn unless you stop them. Find the space.",
        miss: 'Green still wins next turn. Look for four green chips with one gap.',
        answer: (card) => `Green had four in a row with one gap. The ${card} closes it.`,
    },
    fork: {
        label: 'Win in 2',
        task: 'You are blue. Make a move green can’t stop: two winning spaces at once, with a card for each. Green holds no Jacks.',
        miss: 'Green can block that. Find the move that opens two winning spaces at once.',
        answer: (card) => `The ${card} opens two winning spaces. Green can block only one, and you hold the card for the other.`,
    },
};

export const cardName = (card) => {
    if (!card || card === 'blank_card') return 'free corner';
    const [suit, ...rest] = String(card).split('_');
    const value = rest.join(' ').replace('ace simple', 'ace');
    return `${value.charAt(0).toUpperCase()}${value.slice(1)} of ${suit.charAt(0).toUpperCase()}${suit.slice(1)}`;
};

const CORNERS = new Set(['0,0', '0,9', '9,0', '9,9']);

/**
 * The line of five through `pos` that `team` would own with a chip there (the
 * corners count for everyone), to light up once the puzzle is over.
 */
export const lineThrough = (moves, pos, team) => {
    const owner = Object.fromEntries(moves.map(m => [m.pos, m.team]));
    const [x, y] = pos.split(',').map(Number);
    for (const [dx, dy] of [[0, 1], [1, 0], [1, 1], [1, -1]]) {
        for (let start = -4; start <= 0; start++) {
            const cells = [];
            for (let i = 0; i < 5; i++) {
                const cx = x + dx * (start + i);
                const cy = y + dy * (start + i);
                if (cx < 0 || cx > 9 || cy < 0 || cy > 9) break;
                cells.push(`${cx},${cy}`);
            }
            if (cells.length === 5 && cells.every(c => c === pos || CORNERS.has(c) || owner[c] === team)) {
                return cells;
            }
        }
    }
    return [];
};

/** One-eyed Jacks remove a chip; they never place one. */
export const isOneEyedJack = (card) => card === 'spades_jack' || card === 'hearts_jack';

export const shareText = (number, kind, tries, streak) => {
    const solved = tries[tries.length - 1] === 'hit';
    const squares = tries.map(t => (t === 'hit' ? '🟩' : '🟥')).join('');
    const score = solved ? `${tries.length}/${MAX_TRIES}` : `X/${MAX_TRIES}`;
    const lines = [`Sequence Daily #${number} · ${KINDS[kind].label}`, `${squares} ${score}`];
    if (streak > 1) lines.push(`🔥 ${streak}-day streak`);
    return lines.join('\n');
};
